import { useState } from 'react';
import { 
  CognitoIdentityProviderClient,
  SignUpCommand,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  InitiateAuthCommand
} from "@aws-sdk/client-cognito-identity-provider";
import { signIn as amplifySignIn, signOut as amplifySignOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { saveTokensToCache, clearTokensFromCache, getTokensFromCache, shouldRefreshTokens, areTokensValid, clearCognitoCache, saveUserDataToCache } from '../utils/tokenCache';
import { getCognitoUserData } from '../utils/getMergedUserData';

// Configure AWS Cognito client for us-west-2 region
const AWS_REGION = process.env.REACT_APP_AWS_REGION || 'us-west-2';
const client = new CognitoIdentityProviderClient({ 
  region: AWS_REGION 
});
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;

interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export function useAuth() {
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const userAttributes = [{ Name: "email", Value: email }];
      
      if (firstName) {
        userAttributes.push({ Name: "given_name", Value: firstName });
      }
      
      if (lastName) {
        userAttributes.push({ Name: "family_name", Value: lastName });
      }
      
      const command = new SignUpCommand({
        ClientId: CLIENT_ID,
        Username: email,
        Password: password,
        UserAttributes: userAttributes
      });
      
      const response = await client.send(command);
      console.log('SignUp API call successful:', response);
      return true;
    } catch (err: any) {
      // Rethrow. Callers gate the "enter your code" step on this resolving, and
      // App.handleSignUp recovers from UsernameExistsException by resending the
      // code — swallowing the error sent users to the verification screen with
      // no email on the way and nothing recorded server-side.
      console.error('SignUp API call failed:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

    const confirmSignUp = async (email: string, code: string) => {
      setLoading(true);
      setError(null);
      
      try {
        const command = new ConfirmSignUpCommand({
          ClientId: CLIENT_ID,
          Username: email,
          ConfirmationCode: code
        });
        
        await client.send(command);
        return true;
      } catch (err: any) {
        // Rethrow — the modal closes itself on success, so a swallowed
        // CodeMismatchException read as "verified" to the user.
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    };

    const resendConfirmationCode = async (email: string) => {
      setLoading(true);
      setError(null);
      
      try {
        const command = new ResendConfirmationCodeCommand({
          ClientId: CLIENT_ID,
          Username: email
        });
        
        const response = await client.send(command);
        console.log('ResendConfirmationCode API call successful:', response);
        return true;
      } catch (err: any) {
        console.error('ResendConfirmationCode API call failed:', err);
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    };

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      // Ensure Amplify is configured before sign in
      const { configureAmplify, isConfigured } = await import('../../../services/config/amplify');
      
      if (!isConfigured()) {
        const configured = configureAmplify();
        if (!configured) {
          throw new Error('Failed to configure Amplify. Please check environment variables.');
        }
        // Small delay to ensure config is applied
        await new Promise(resolve => setTimeout(resolve, 200));
             }
      
      // Check if there's already a signed in user and sign them out first
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          await amplifySignOut();
          // Small delay to ensure sign out is complete
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (error) {
        // No current user, which is what we want
      }

      // Use Cognito InitiateAuthCommand directly to get response with expiry times
      // The response includes ExpiresIn (seconds) and TokenType in AuthenticationResult
      const authCommand = new InitiateAuthCommand({
        ClientId: CLIENT_ID,
        AuthFlow: 'USER_PASSWORD_AUTH',
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password
        }
      });

      const authResponse = await client.send(authCommand);
      
      if (authResponse.AuthenticationResult) {
        const authResult = authResponse.AuthenticationResult;
        const idToken = authResult.IdToken;
        const accessToken = authResult.AccessToken;
        const refreshToken = authResult.RefreshToken;
        const expiresIn = authResult.ExpiresIn; // Expiry in seconds from response
        const tokenType = authResult.TokenType || 'Bearer'; // Token type from response (usually 'Bearer')
        
        if (idToken && accessToken && expiresIn !== undefined) {
          // Use Amplify's signIn to set up the auth session for REST API calls
          // This will create CognitoIdentityServiceProvider.* entries in localStorage
          try {
            await amplifySignIn({
              username: email,
              password: password
            });
            
            // Save metadata (expiry and token type) after Amplify creates Cognito cache entries
            const now = Date.now();
            const tokenExpiry = now + (expiresIn * 1000);
            
            saveTokensToCache(
              expiresIn, // Use ExpiresIn from Cognito response
              tokenType // Use TokenType from Cognito response (usually 'Bearer')
            );
            
            // Fetch and cache Cognito user data after successful login
            try {
              const userData = await getCognitoUserData(accessToken);
              if (userData) {
                saveUserDataToCache(userData, tokenExpiry);
                console.log('✅ Cognito user data fetched and cached after login');
              } else {
                console.warn('⚠️ Failed to fetch Cognito user data after login');
              }
            } catch (userDataError) {
              console.warn('⚠️ Error fetching Cognito user data after login:', userDataError);
              // Don't fail login if user data fetch fails
            }
          } catch (amplifyError) {
            // If Amplify signIn fails but Cognito auth succeeded, log warning but continue
            console.warn('⚠️ Amplify signIn failed after Cognito auth:', amplifyError);
            // Still save metadata even if Amplify signIn fails
            const now = Date.now();
            const tokenExpiry = now + (expiresIn * 1000);
            saveTokensToCache(
              expiresIn, // Use ExpiresIn from Cognito response
              tokenType // Use TokenType from Cognito response (usually 'Bearer')
            );
            
            // Fetch and cache Cognito user data even if Amplify signIn failed
            try {
              const userData = await getCognitoUserData(accessToken);
              if (userData) {
                saveUserDataToCache(userData, tokenExpiry);
                console.log('✅ Cognito user data fetched and cached after login');
              }
            } catch (userDataError) {
              console.warn('⚠️ Error fetching Cognito user data after login:', userDataError);
            }
          }
          
          // Set tokens state for backward compatibility
          const authTokens = {
            accessToken: accessToken,
            idToken: idToken,
            refreshToken: refreshToken || ''
          };
          
          setTokens(authTokens);
          console.log('✅ Tokens saved to cache with expiry times from Cognito response:', {
            expiresIn: `${expiresIn} seconds`,
            tokenType: tokenType
          });
          return true;
        } else {
          console.error('Missing required tokens or expiry in Cognito response');
          setError('Authentication failed - tokens not available in response');
          return false;
        }
      } else {
        console.error('No AuthenticationResult in Cognito response');
        setError('Authentication failed - no authentication result');
        return false;
      }
    } catch (err: any) {
      console.error('SignIn API call failed:', err);
      setError(err.message);
      // Re-throw the error so it can be handled by the caller (e.g., App.tsx)
      // This allows proper error handling like switching to signup for UserNotFoundException
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshTokens = async (): Promise<boolean> => {
    try {
      const { getRefreshToken, saveTokensToCache, clearTokensFromCache } = await import('../utils/tokenCache');
      const refreshToken = getRefreshToken();
      
      if (!refreshToken) {
        console.error('❌ No refresh token available');
        return false;
      }

      // Use Cognito REFRESH_TOKEN_AUTH flow to refresh tokens
      const refreshCommand = new InitiateAuthCommand({
        ClientId: CLIENT_ID,
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        AuthParameters: {
          REFRESH_TOKEN: refreshToken
        }
      });

      const refreshResponse = await client.send(refreshCommand);
      
      if (refreshResponse.AuthenticationResult) {
        const authResult = refreshResponse.AuthenticationResult;
        const idToken = authResult.IdToken;
        const accessToken = authResult.AccessToken;
        // Note: Refresh token may or may not be returned in refresh response
        const newRefreshToken = authResult.RefreshToken || refreshToken; // Use old refresh token if not provided
        const expiresIn = authResult.ExpiresIn;
        const tokenType = authResult.TokenType || 'Bearer';
        
        if (idToken && accessToken && expiresIn !== undefined) {
          // Update Amplify session - this will update Cognito cache entries
          // Note: Amplify doesn't have a direct refresh method, so we need to maintain session
          try {
            const session = await fetchAuthSession({ forceRefresh: true });
            if (!session.tokens) {
              console.warn('⚠️ Amplify session refresh failed, but tokens were refreshed');
            } else {
              // Save metadata after Amplify updates Cognito cache entries
              const now = Date.now();
              const tokenExpiry = now + (expiresIn * 1000);
              
              saveTokensToCache(
                expiresIn, // Use ExpiresIn from Cognito response
                tokenType // Use TokenType from Cognito response (usually 'Bearer')
              );
              
              // Refresh user data in cache when tokens are refreshed
              try {
                const userData = await getCognitoUserData(accessToken);
                if (userData) {
                  saveUserDataToCache(userData, tokenExpiry);
                  console.log('✅ Cognito user data refreshed in cache');
                }
              } catch (userDataError) {
                console.warn('⚠️ Error refreshing Cognito user data:', userDataError);
              }
            }
          } catch (amplifyError) {
            console.warn('⚠️ Amplify session refresh warning:', amplifyError);
            // Still save metadata even if Amplify session refresh fails
            const now = Date.now();
            const tokenExpiry = now + (expiresIn * 1000);
            
            saveTokensToCache(
              expiresIn, // Use ExpiresIn from Cognito response
              tokenType // Use TokenType from Cognito response (usually 'Bearer')
            );
            
            // Refresh user data in cache even if Amplify session refresh failed
            try {
              const userData = await getCognitoUserData(accessToken);
              if (userData) {
                saveUserDataToCache(userData, tokenExpiry);
                console.log('✅ Cognito user data refreshed in cache');
              }
            } catch (userDataError) {
              console.warn('⚠️ Error refreshing Cognito user data:', userDataError);
            }
          }
          
          // Update tokens state
          const authTokens = {
            accessToken: accessToken,
            idToken: idToken,
            refreshToken: newRefreshToken
          };
          
          setTokens(authTokens);
          console.log('✅ Tokens refreshed successfully:', {
            expiresIn: `${expiresIn} seconds`,
            tokenType: tokenType
          });
          return true;
        } else {
          console.error('Missing required tokens or expiry in refresh response');
          return false;
        }
      } else {
        console.error('No AuthenticationResult in refresh response');
        return false;
      }
    } catch (err: any) {
      console.error('❌ Token refresh failed:', err);
      
      // If refresh fails (e.g., refresh token expired), clear tokens
      if (err.name === 'NotAuthorizedException' || err.name === 'TokenRefreshException') {
        console.log('🔄 Refresh token expired, clearing cached tokens');
        clearTokensFromCache();
        setTokens(null);
      }
      
      return false;
    }
  };

  /**
   * Ensure tokens are valid, refresh if needed
   * @param forceRefresh - Force refresh even if tokens are still valid
   */
  const ensureValidTokens = async (forceRefresh: boolean = false): Promise<boolean> => {
    const { shouldRefreshTokens, areTokensValid } = await import('../utils/tokenCache');
    
    // If force refresh or tokens need refresh, refresh them
    if (forceRefresh || !areTokensValid() || shouldRefreshTokens()) {
      console.log('🔄 Refreshing tokens...');
      return await refreshTokens();
    }
    
    return true; // Tokens are still valid
  };

  const signOut = async () => {
    try {
      await amplifySignOut();
      setTokens(null);
      // Clear our custom token cache
      clearTokensFromCache();
      // Clear Amplify/Cognito default cache entries
      clearCognitoCache();
      // Also clear legacy localStorage item for backward compatibility
      localStorage.removeItem('auth-tokens');
    } catch (error) {
      console.error('❌ Sign out error:', error);
      // Still clear caches even if Amplify signOut fails
      clearTokensFromCache();
      clearCognitoCache();
      localStorage.removeItem('auth-tokens');
    }
  };

  return {
    tokens,
    loading,
    error,
    signUp,
    confirmSignUp,
    resendConfirmationCode,
    signIn,
    signOut,
    refreshTokens,
    ensureValidTokens,
    isAuthenticated: !!tokens
  };
}